/* global GM_STATE */
import { useEffect, useMemo, useState } from 'preact/hooks'
import { createPortal } from 'preact/compat'
import { formatMoney } from 'libs/shopify'
import { emitterProps, globalEmitter } from 'helpers/emitter-event'
import { cdnImageUrl } from 'helpers/cdn-image.mjs'
import {
  CartLineQuantitySelect,
  CartLineSizeSelect,
  hasSizeChoice,
  lineOptionCaption,
  lineSizeLabel
} from 'snippets/cart-line-select/cart-line-select'
import { matchSpendAndSaveTier, formatSpendAndSaveMessage } from 'helpers/spend-and-save.mjs'
import useCartGlobal, { CartConfigKeys } from 'hooks/useCartGlobal'
import PreactImage from 'snippets/image/image'
import Icon from 'snippets/icon/icon'
import LoaderSpin from 'snippets/loader-spin/loader-spin'
import register from 'preact-custom-element'
import { cart as cartClass } from '@/components/helpers/cart'
import { PromoLine } from '../promo-line/render-promo-line'
// import { Badge } from '../badge/badge-render'
// import { useBadges } from '../badge/use-badge'
import { usePromoLines } from '../promo-line/use-promo-lines'
import { useOmneoClient } from '@/components/libs/omneo/hooks/useOmneoClient'
import { useRewardBalance } from '@/components/libs/omneo/hooks/useRewardBalance'
import 'snippets/recommendations-grid/recommendations-grid'

/**
 *
 * Size, quantity and removal for a line. The two dropdowns own their own cart mutation
 * (cart-line-select.js, shared with the cart page); this only drives the drawer's loading state
 * and surfaces failures through the global notice.
 *
 * @param {*} param0
 * @returns
 */
const MiniCartItemControls = ({ item, handleSetCartConfig }) => {
  const setLoading = (value) => handleSetCartConfig(CartConfigKeys.isLoading, value)

  const onStart = () => setLoading(true)

  const onDone = async () => {
    /** The mutation refreshes the singleton, but the drawer renders from cart config. */
    await cartClass.getCart()
    handleSetCartConfig(CartConfigKeys.cart, cartClass.value)
    setLoading(false)
  }

  const onError = (error) => {
    globalEmitter.emit(emitterProps.notice.global, {
      type: 'error',
      content: error.message
    })
    setLoading(false)
  }

  const onRemove = async () => {
    try {
      onStart()
      await cartClass.updateLineItemQuantity({ key: item.key, id: item.variant_id, quantity: 0 }, { silent: true })
      await onDone()
    } catch (error) {
      onError(error)
    }
  }

  return (
    <div class="flex flex-col md:flex-row items-start md:items-center justify-between gap-0 md:gap-3 pt-2 md:pt-4">
      <div class="flex flex-row items-center gap-2">
        {hasSizeChoice(item) && (
          <CartLineSizeSelect item={item} onStart={onStart} onDone={onDone} onError={onError} />
        )}
        <CartLineQuantitySelect item={item} onStart={onStart} onDone={onDone} onError={onError} />
      </div>
      <button
        type="button"
        onClick={onRemove}
        class="btn btn-text-black btn-sm btn-inline paragraph-sm py-0 pl-0 md:pl-2 md:py-2 pr-0 text-right underline underline-offset-4 opacity-70"
      >
        Remove
      </button>
    </div>
  )
}

/**
 * Renders a region into its theme block's slot.
 *
 * Returns null when the slot is absent, so a merchant who removes a block in the editor loses that
 * region rather than breaking the panel.
 *
 * The slots are static blocks inside <mini-cart>. Verified 2026-09-02 that they survive
 * preact-custom-element's mount and are resolvable during the first render, which is also what
 * makes `slotEnabled` below safe — anything the portal can find, it can read attributes off.
 */
const MiniCartPortal = ({ targetId, children }) => {
  const target = document.getElementById(targetId)
  return target ? createPortal(children, target) : null
}

/**
 * Reads a checkbox setting a block emitted onto its slot element. Liquid writes `true`/`false`, so
 * only an explicit `false` hides — a missing attribute or missing slot leaves the element showing.
 */
const slotEnabled = (targetId, name) => document.getElementById(targetId)?.dataset[name] !== 'false'

const MiniCartMessage = ({ message, onSuccessMessage, goalReached, children }) => {
  const Message = ({ message }) => {
    return <p class="paragraph-xs text-center" dangerouslySetInnerHTML={{ __html: message }}></p>
  }

  const SuccessMessage = ({ onSuccessMessage }) => {
    return (
      <div className={'flex flex-row gap-2'}>
        <p class="paragraph-xs text-center font-bold" dangerouslySetInnerHTML={{ __html: onSuccessMessage }}></p>
      </div>
    )
  }

  return (
      <div data-role="free_shipping_monitor" class={`flex flex-col gap-2 ${goalReached ? 'bg-[#6CCB45]' : 'bg-[#FDC145]'} py-4 px-4 rounded-lg basis-full md:basis-1/2 h-full items-center justify-center`}>
        {goalReached ? <SuccessMessage onSuccessMessage={onSuccessMessage} /> : <Message message={message} />}
        {!goalReached && children}
      </div>
  )
}

/**
 *
 *
 * Mini Cart Free Shipping,
 * Currently hardcoded to handle the free shipping discount range.
 *
 * @param {*} param0
 * @returns
 */
function MiniCartFreeShipping({ cartSubTotal, freeShippingGoal }) {
  const handleFreeShippingValue = (amount) => {
    if (typeof amount === 'number' && amount > 0) {
      return amount
    }
    return parseInt(amount)
  }
  const freeShippingMinimum = handleFreeShippingValue(freeShippingGoal) ?? 10000
  const reachedGoal = freeShippingMinimum - cartSubTotal
  const progressBarPercentage = Math.floor((cartSubTotal / freeShippingMinimum) * 100)
  const progressBarPercentageWidth = progressBarPercentage > 100 ? '100' : progressBarPercentage
  return (
    <MiniCartMessage
      onSuccessMessage={'You get free standard shipping on this order.'}
      message={`<b>Spend ${formatMoney(reachedGoal)}</b> for free shipping`}
      goalReached={progressBarPercentageWidth >= 100}
    >
      <div class="relative h-1.5 rounded-full w-full bg-primary-grey overflow-hidden">
        <div
          style={{ width: progressBarPercentageWidth + '%' }}
          class="absolute left-0 h-full bg-black transition-all"
        ></div>
      </div>
    </MiniCartMessage>
  )
}

/**
 *
 * Spend & Save monitor. Mirrors the free shipping bar, driven by the `spend_and_save`
 * metaobject tiers. Below the next unreached tier it shows that tier's message with `[money]`
 * replaced by the amount still required; past the highest tier it shows the success message.
 *
 * Compares against cart.total_price (post-discount), not the item subtotal the free shipping
 * bar uses — tiers are defined on what the customer actually pays. Tiers arrive unsorted from
 * parse-spend-and-save.liquid, so the numeric ordering happens here.
 *
 * The match is resolved by `resolveSpendAndSave` rather than inside the component, because the
 * rewards banner below has to know whether a tier is showing before it decides to render.
 *
 * @param {*} param0
 * @returns
 */
function resolveSpendAndSave (cartTotal, tiers = GM_STATE?.metaobjects?.spend_and_save) {
  const match = matchSpendAndSaveTier(cartTotal, tiers, GM_STATE?.localization?.country)
  if (!match) return null

  const { tier, goalReached, remaining, percentage } = match
  const message = goalReached ? tier.successMessage : tier.message
  const messageHtml = formatSpendAndSaveMessage(message, `<b>${formatMoney(remaining)}</b>`)

  if (!messageHtml) return null

  return {
    goalReached,
    percentage,
    messageHtml,
    textColour: goalReached ? tier.successTextColour : tier.textColour,
    bgColour: goalReached ? tier.successBackgroundColour : tier.backgroundColour
  }
}

function MiniCartSpendAndSave ({ content }) {
  const { goalReached, percentage, messageHtml, textColour, bgColour } = content

  return (
    <div
      data-role="spend_and_save_monitor"
      class={`flex flex-col gap-2 py-4 px-4 rounded-lg basis-full md:basis-1/2 h-full items-center justify-center ${
        bgColour ? 'bg-[--ss-bg-colour]' : 'bg-secondary-yellow/20'
      } ${textColour ? 'text-[--ss-text-colour]' : ''}`}
      style={{ '--ss-text-colour': textColour || '', '--ss-bg-colour': bgColour || '' }}
    >
      <div class="flex flex-row items-center justify-center gap-2">
        <p class="paragraph-xs text-center text-balance font-bold" dangerouslySetInnerHTML={{ __html: messageHtml }}></p>
      </div>
      {!goalReached && (
        <div class="relative h-1.5 rounded-full w-full bg-primary-white overflow-hidden">
          <div
            style={{ width: percentage + '%' }}
            class="absolute left-0 h-full bg-black transition-all"
          ></div>
        </div>
      )}
    </div>
  )
}

/** active stays false until the mini cart drawer is opened, so the Omneo-backed child below mounts only then. */
function CartRewardsBanner ({ cartTotal, isMiniCart = true, cart, active }) {
  const giftCardTotal = useMemo(() => cart.items?.reduce((acc, item) => {
    if (item.handle.includes('gift-card')) {
      return acc + item.line_price
    }
    return acc
  }, 0), [cart?.items])

  // cart.total_price, not items_subtotal_price: the latter is pre-discount and overstated the earn
  // figure by the whole Spend & Save reduction. Gift-card line_price is pre-discount, hence the clamp.
  const rewardReceiveAmount = useMemo(
    () => Math.max(0, cartTotal - giftCardTotal) * ((GM_STATE?.rewards?.percentage ?? 10) / 100),
    [cartTotal, giftCardTotal]
  )

  if (!active || rewardReceiveAmount <= 0) return null

  return <CartRewardsContent rewardReceiveAmount={rewardReceiveAmount} isMiniCart={isMiniCart} />
}

function CartRewardsContent ({ rewardReceiveAmount, isMiniCart }) {
  const { client } = useOmneoClient()
  const [rewardBalance, setRewardBalance] = useState(0) // in cents

  const refreshBalance = () => {
    setTimeout(() => {
      client.profileClient.getBalance().then((data) => {
        if (!data || typeof data.reward_balance !== 'number') return
        setRewardBalance(data.reward_balance * 100)
      })
    }, 2000)
  }

  useEffect(() => {
    if (!client?.client) return
    refreshBalance()
  }, [client])

  useEffect(() => {
    if (!client?.client) return
    window.addEventListener('omneo:refresh-balance', refreshBalance)

    return () => {
      window.removeEventListener('omneo:refresh-balance', refreshBalance)
    }
  }, [client])

  const isLoggedIn = !!GM_STATE?.customer?.logged

  // A member with a balance is told what they can spend now instead of what this order earns —
  // one line in one card. Balance arrives ~2s after mount, so the earn line shows until it lands.
  const showBalance = isLoggedIn && rewardBalance > 0

  return (
    <div class="basis-full md:basis-1/2 w-full">
      {!GM_STATE?.rewards?.hideBanner && (
        <div
          data-role="free_shipping_monitor"
          class="flex flex-row md:flex-col justify-center items-center gap-2 bg-secondary-violet/100 px-4 pt-4 pb-4 rounded-lg h-full"
        >
          {showBalance ? (
              <p className="flex-1 paragraph-xs text-left md:text-center text-primary-black text-balance">
                You have <b>{formatMoney(rewardBalance).replace(/\.00$/, '')}</b> of Fit Club rewards available to spend.
              </p>
          ) : (
            <p className="flex-1 paragraph-xs text-left md:text-center text-primary-black text-balance">
              Earn <b>{formatMoney(rewardReceiveAmount).replace(/\.00$/, '')} rewards</b> with Fit Club
            </p>
          )}

          {!isLoggedIn && (
            <a
              href="#"
              class="btn btn-text-black btn-sm btn-inline paragraph-sm my-0 h-auto font-medium underline leading-none text-center"
              onClick={(e) => {
                e.preventDefault()
                document.querySelector('.mini-cart-close-button')?.click()
                window.openLoginDrawer()
              }}
            >
              Login or join
            </a>
          )}
        </div>
      )}
    </div>
  )
}

function CartPageRewardsBanner () {
  const { cartConfig } = useCartGlobal()
  const { cart } = cartConfig

  return <CartRewardsBanner cartTotal={cart.total_price} isMiniCart={false} cart={cart} active />
}

/**
 *
 * MiniCart modal Top Banner
 *
 * @param {\} param0
 * @returns
 */
const MiniCartHeader = ({ itemCount }) => {
  const heading = 'Your Bag'
  return (
    <div class="border-b border-b-solid border-b-[#E0DED8] sticky top-0 z-20 flex items-center justify-between bg-white pl-6 pr-3">
      <div class="py-4">
      <h4 class="h4 text-lg font-[700]">
        {heading} {itemCount > 0 && <span class="text-sm font-[700] text-[#696865]">({itemCount} item{itemCount > 1 ? 's' : ''})</span>}
      </h4>
      </div>

      <button type="button" class="mini-cart-close-button cursor-pointer p-4" data-modal-parent="mini-cart" data-modal-role="close">
        <Icon id="close" className="min-h-2 min-w-2 pointer-events-none" />
      </button>
    </div>
  )
}

/**
 *
 * Display line item price
 * Handles different price if discount is applied
 *
 * Props Cart Line Item
 *
 * @param {*} param0
 * @returns
 */
const ItemPrice = ({ item }) => {
  const DiscountedPrice = ({ newPrice, oldPrice }) => {
    return (
      <>
        <p class="paragraph-sm font-bold text-primary-sale">{formatMoney(newPrice)}</p>
        <s class="paragraph-sm text-right text-neutrals-secondary-grey">{formatMoney(oldPrice)}</s>
      </>
    )
  }

  if (item.discounts.length > 0) {
    return <DiscountedPrice newPrice={item.line_price} oldPrice={item.original_line_price} />
  } else if (item.compare_at_price > item.original_price) {
    return <DiscountedPrice newPrice={item.line_price} oldPrice={item.compare_at_price * item.quantity} />
  }
  return <p class="paragraph-sm font-bold">{formatMoney(item.original_line_price)}</p>
}

const MiniCartItemDiscount = ({ item }) => {
  return (
    <>
      {item.discounts.map((discount) => {
        return (
          <p class="paragraph-xs w-fit -rotate-1 bg-secondary-yellow px-2 py-1 font-bold uppercase text-primary-black">
            {discount.title}
          </p>
        )
      })}
    </>
  )
}

/** The slot is 192px wide (`w-48`), so 384 covers it at 2x. */
const MINI_CART_THUMB_WIDTH = 384

/**
 *
 * Displays line item image
 *
 * @param {*} imageSrc
 * @param {*} id
 * @param {*} productPath
 * @returns
 */
const MiniCartItemImage = ({ imageSrc, id, productPath }) => {
  return (
    <a href={productPath} className="flex-grow-0 w-52 md:w-48 overflow-hidden rounded-md aspect-1 md:max-h-36">
      <PreactImage
        imageClass="fit object-bottom"
        className={'h-full w-full object-cover'}
        key={id}
        imageUrl={[cdnImageUrl(imageSrc, MINI_CART_THUMB_WIDTH)]}
      />
    </a>
  )
}

/**
 *
 * Display the line item
 * 1) Title
 * 2) Variant Details (size, colour)
 * 3) Discount details (if applicable)
 * 4) Line Item price
 *
 * @param {*} param0
 * @returns
 */
const MiniCartItemDetails = ({ item }) => {
  /** Both come from cart-json.liquid; the split-on-last-space fallback is for a cached response. */
  const title = item.customer_facing_product_name || item.product_title?.slice(0, item.product_title.lastIndexOf(' '))
  const colour = item.is_gift_card ? null : item.colour || item.product_title?.slice(item.product_title.lastIndexOf(' ') + 1)

  /** Size (a gift card's amount) owns a dropdown in the controls row unless the product has only one, so it is skipped here. */
  const staticOptions = item.is_gift_card ? [] : item.options_with_values.filter((option) => option.name !== 'Size')
  const sizeLabel = hasSizeChoice(item) ? null : lineSizeLabel(item)

  return (
    <div class="flex flex-row justify-between">
      <div class="flex flex-col gap-2">
        <MiniCartItemDiscount item={item} />

        <div>
          <p class="paragraph-sm w-4/5 font-bold">{title}</p>
          {colour && <p class="paragraph-xs text-neutrals-secondary-grey hidden md:block">Colour: {colour}</p>}
          {sizeLabel && <p class="paragraph-xs text-neutrals-secondary-grey">{lineOptionCaption(item)}: {sizeLabel}</p>}
          {staticOptions.map((option) => (
            <p class="paragraph-xs text-neutrals-secondary-grey hidden md:block">
              {option.name}: {option.value}
            </p>
          ))}
        </div>
      </div>
      <div class="flex flex-col gap-1">
        <ItemPrice item={item} />
      </div>
    </div>
  )
}

const MiniCartLineItem = ({ item, handleSetCartConfig, isLoading }) => {
  const loadingState = isLoading && 'opacity-45'
  const criteria = {
    tags: item?.tags ?? [],
    productIds: [item.product_id],
    collectionIds: item.collections.map((c) => c.id.toString())
  }

  // const [badge] = useBadges(criteria)
  const [promoLine] = usePromoLines(criteria)

  return (
    <>
      <div class={`flex flex-row gap-2 transition-all ${loadingState}`}>
        <div class="flex flex-row gap-2 p-4 bg-white rounded-lg w-full">
        <MiniCartItemImage imageSrc={item.featured_image.url} id={item.id} productPath={item.url} />

        {/* <Badge badge={badge} /> */}
        <div class="flex w-full flex-grow flex-col justify-between md:pl-2">
          {promoLine && (
            <div class={'pb-2'}>
              <PromoLine promoline={promoLine} />
              {/* <Badge badge={badge} /> */}
            </div>
          )}

          <MiniCartItemDetails item={item} />
          <MiniCartItemControls item={item} handleSetCartConfig={handleSetCartConfig} />
        </div>
        </div>
      </div>
    </>
  )
}

/**
 *
 * Displays cart total for Mini Cart.
 *
 * @param {*} param0
 * @returns
 */
const DisplayCartTotal = ({ total, totalCartDiscount, isLoading }) => {
  const cartTotal = formatMoney(total)
  const totalDiscount = formatMoney(totalCartDiscount)

  return (
    <div class="flex flex-col justify-between">
      {totalCartDiscount > 0 && (
        <div class="flex flex-row justify-between px-4 pt-4 pb-0 md:px-4 md:pt-4">
          <p class="paragraph-sm">Savings</p>
          <p class={`paragraph-sm text-primary-sale transition-opacity duration-200 ${isLoading && 'opacity-30'}`}>
            {totalDiscount}
          </p>
        </div>
      )}
      <div class="flex flex-row justify-between px-4 pt-4 pb-0 md:px-4 md:pt-4">
        <h4 class="paragraph-sm font-bold">Subtotal</h4>
        <h4 class={`paragraph-sm font-bold transition-opacity duration-200 ${isLoading && 'opacity-30'}`}>{cartTotal}</h4>
      </div>
    </div>
  )
}

/**
 * The spendable Fit Club balance, as the last line of a cart summary. Same balance the header badge
 * shows (`NavigationProfileIcon`), which is why nothing renders for a logged-out customer or a zero
 * balance — there is nothing to spend.
 *
 * Horizontal padding comes from the call site: the drawer's summary pads its own rows, the cart
 * page pads the whole panel. Registered as `x-rewards-at-checkout` for the cart page, where
 * `active` defaults true; the drawer passes its opened state so Omneo is not woken for a panel
 * nobody has looked at.
 *
 * @param {*} param0
 * @returns
 */
const RewardsAtCheckout = ({ active = true, className = '' }) => {
  const balance = useRewardBalance(active)

  return (
    <div class={`border-t border-neutrals-medium-grey pt-4 ${className}`}>
      <p class={`paragraph-sm font-bold ${balance > 0 ? 'text-secondary-green' : 'text-primary-black'} flex items-center gap-2 justify-between`}>
        <span>Rewards available at checkout</span> <span class="">{formatMoney(balance).replace(/\.00$/, '')}</span>
      </p>
    </div>
  )
}

/**
 *
 * Display load state of the mini cart
 *
 * @param {*} param0
 * @returns
 */
const CartLoading = ({ isLoading }) => {
  return isLoading && <LoaderSpin wrapperClass="bg-transparent" />
}

const CheckoutButton = ({ checkoutButtonTitle }) => {
  return (
    <div class="p-4 pb-2">
      <a class="btn btn-md btn-solid-black justify-center" href="/checkout">
        {checkoutButtonTitle}
      </a>
    </div>
  )
}

/**
 *
 * Displays the line items present in the the customers cart.
 *
 * @param {*} param0
 * @returns
 */
const CartLineItems = ({ cart, handleSetCartConfig, isLoading }) => {
  return (
    <div className="flex flex-col gap-2 bg-primary-grey py-6 px-4" data-role="cart_items">
      {cart.items.map((item) => {
        return <MiniCartLineItem item={{ ...item }} handleSetCartConfig={handleSetCartConfig} isLoading={isLoading} />
      })}
    </div>
  )
}

function addCartEventListeners(handleSetCartConfig) {
  const handleCartUpdateEvent = async () => {
    try {
      handleSetCartConfig(CartConfigKeys.isLoading, true)

      await cartClass.getCart()
      handleSetCartConfig(CartConfigKeys.cart, cartClass.value)
    } catch (error) {
      console.error('Failed to update cart:', error)
    } finally {
      handleSetCartConfig(CartConfigKeys.isLoading, false)
    }
  }

  // Remove any existing listeners first to prevent duplication
  document.removeEventListener(cartClass.CART_UPDATE_EVENT, handleCartUpdateEvent)

  // Add the event listener
  document.addEventListener(cartClass.CART_UPDATE_EVENT, handleCartUpdateEvent)
}

/**
 *
 * Mini Cart
 *
 * @returns
 */
function MiniCart() {
  const { cartConfig, handleSetCartConfig } = useCartGlobal()
  const { isLoading, cart } = cartConfig
  const freeShippingMinimum = GM_STATE.cart.freeShippingMinimum

  useEffect(() => addCartEventListeners(handleSetCartConfig), [])

  /** The panel is in the DOM on every page but closed. Only wake Omneo once it is opened. */
  const [hasOpened, setHasOpened] = useState(false)

  useEffect(() => {
    const modal = document.querySelector('[data-id="mini-cart"]')
    if (!modal) return
    if (modal.dataset.ui === 'visible') {
      setHasOpened(true)
      return
    }
    const observer = new MutationObserver(() => {
      if (modal.dataset.ui !== 'visible') return
      setHasOpened(true)
      observer.disconnect()
    })
    observer.observe(modal, { attributes: true, attributeFilter: ['data-ui'] })
    return () => observer.disconnect()
  }, [])

  /** Which bars the Minicart Shipping block has enabled. The slot outlives every re-render. */
  const shippingElements = useMemo(
    () => ({
      freeShipping: slotEnabled('minicart-shipping', 'freeShipping'),
      spendAndSave: slotEnabled('minicart-shipping', 'spendAndSave'),
      rewards: slotEnabled('minicart-shipping', 'rewards')
    }),
    []
  )

  /** Resolved here, not in the bar, so the rewards banner below can stand down when a tier shows. */
  const spendAndSave = useMemo(() => resolveSpendAndSave(cart.total_price), [cart.total_price])
  const spendAndSaveShowing = shippingElements.spendAndSave && !!spendAndSave

  const totalFullPrice = useMemo(() => {
    return cart.items.reduce((total, item) => {
      const itemFullPrice = item?.compare_at_price || item.price
      return total + itemFullPrice * item.quantity
    }, 0)
  }, [cart])

  const totalDiscount = useMemo(() => {
    return totalFullPrice - cart.total_price
  }, [cart, totalFullPrice])

  return (
    <>
      <MiniCartPortal targetId="minicart-header">
        <MiniCartHeader itemCount={cart.item_count} />
      </MiniCartPortal>
      {/* Not portalled: an absolutely positioned overlay over the whole panel, not a region. */}
      <CartLoading isLoading={isLoading} />
      <MiniCartPortal targetId="minicart-shipping">
        {/* Rewards / Free shipping / Tiered wrapper for different states and devices */}
        <div class={`flex flex-col gap-2 px-4 pt-6 border-t border-neutrals-medium-grey ${cart?.items?.length ? 'md:flex-row' : ''}`}>
          {shippingElements.freeShipping && (
            <MiniCartFreeShipping cartSubTotal={cart.items_subtotal_price} freeShippingGoal={freeShippingMinimum} />
          )}
          {spendAndSaveShowing && <MiniCartSpendAndSave content={spendAndSave} />}
          {/* Spend & Save replaces the rewards banner rather than sitting beside it — three bars is a wall. */}
          {shippingElements.rewards && !spendAndSaveShowing && (
            <CartRewardsBanner cartTotal={cart.total_price} cart={cart} active={hasOpened} />
          )}
        </div>
      </MiniCartPortal>
      {cart?.items?.length ? (
        <>
          <MiniCartPortal targetId="minicart-items">
            <CartLineItems cart={cart} isLoading={isLoading} handleSetCartConfig={handleSetCartConfig} />
          </MiniCartPortal>
          <MiniCartPortal targetId="minicart-summary">
            {/* Pinning is the Liquid wrapper's job (mini-cart.liquid) — it holds the express buttons too. */}
            <div class="flex flex-col">
              <DisplayCartTotal totalCartDiscount={totalDiscount} total={cart.total_price} isLoading={isLoading} />
              <RewardsAtCheckout active={hasOpened} className="mx-4 mt-4" />
              <CheckoutButton checkoutButtonTitle={GM_STATE.cart.checkoutButtonTitle} />
            </div>
          </MiniCartPortal>
        </>
      ) : null}
    </>
  )
}
register(MiniCart, 'mini-cart')
register(CartPageRewardsBanner, 'cart-rewards-banner')
register(RewardsAtCheckout, 'x-rewards-at-checkout')
